import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import { inpatientAPI, petAPI, clinicAPI, clinicAppointmentsAPI } from "../../services/api";
import { usePaymentTypes } from "../../hooks/usePaymentTypes";
import SelectPaymentModal from "./SelectPaymentModal"; // Commented out for now - will be used later
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";
import { USER_TYPES } from "../../constants/userTypes";
import SpecialRequestsNoteModal from "./SpecialRequestsNoteModal";
import QuickAddPetModal from "./QuickAddPetModal";

const AdmitInpatientModal = ({
  isOpen,
  onClose,
  clinic = null,
  vets = [],
  inpatientVisits = [],
  outpatientVisits = [],
  onVisitCreated,
  // Edit mode support
  isEdit = false,
  initialVisit = null,
  onVisitUpdated,
  // Prefill from new patient wizard
  prefillPetOwner = null,
  prefillPet = null,
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const formatDate = useCallback(
    (value) => {
      if (!value) return "";
      try {
        return new Date(value).toLocaleDateString(i18n.language, {
          year: "numeric",
          month: "short",
          day: "numeric",
          calendar: "gregory"
        });
      } catch (error) {
        return value;
      }
    },
    [i18n.language]
  );

  const dayNamesShort = useMemo(
    () => [
      t("receptionInpatients.admit.dayShort.sun"),
      t("receptionInpatients.admit.dayShort.mon"),
      t("receptionInpatients.admit.dayShort.tue"),
      t("receptionInpatients.admit.dayShort.wed"),
      t("receptionInpatients.admit.dayShort.thu"),
      t("receptionInpatients.admit.dayShort.fri"),
      t("receptionInpatients.admit.dayShort.sat")
    ],
    [t]
  );

  const getDayShort = useCallback(
    (index) => dayNamesShort[index] || "",
    [dayNamesShort]
  );

  const formatStatusLabel = useCallback(
    (status) => {
      if (!status) return t("receptionInpatients.tab.status.unknown");
      const key = status.toLowerCase();
      return t(`receptionInpatients.tab.status.${key}`, { defaultValue: status });
    },
    [t]
  );

  const getPetOwnerDisplay = useCallback((value) => {
    if (typeof value === "string") {
      return value.trim();
    }
    return (
      value?.display_name ||
      value?.full_name ||
      `${value?.first_name || ""} ${value?.last_name || ""}`.trim() ||
      ""
    );
  }, []);

  const getPetNameDisplay = useCallback((value) => {
    if (typeof value === "string") {
      return value.trim();
    }
    return (value?.name || "").trim();
  }, []);

  const getVetDisplay = useCallback((value) => {
    if (typeof value === "string") {
      return value.trim();
    }
    return (
      value?.user?.name ||
      value?.name ||
      value?.full_name ||
      `${value?.first_name || ""} ${value?.last_name || ""}`.trim() ||
      ""
    );
  }, []);

  const [newInpatient, setNewInpatient] = useState({
    id: null,
    pet_owner: "",
    pet: "",
    vet: "",
    reason: "",
    date: "",
    time: "",
    cage_no: "",
    allowed_food: "",
    allowed_fluids: "",
  });
  const [admitInpatientDownpayment, setAdmitInpatientDownpayment] = useState(0.0);
  const [petOwnerSearch, setPetOwnerSearch] = useState("");
  const [selectedPetOwner, setSelectedPetOwner] = useState(null);
  const [petOwnerPets, setPetOwnerPets] = useState([]);
  const [isLoadingPets, setIsLoadingPets] = useState(false);
  const [showAddPetModal, setShowAddPetModal] = useState(false);
  const [petOwnerSearchResults, setPetOwnerSearchResults] = useState([]);
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [isSearchingPetOwners, setIsSearchingPetOwners] = useState(false);
  const [vetSearch, setVetSearch] = useState("");
  const [selectedVet, setSelectedVet] = useState(null);
  const [showVetDropdown, setShowVetDropdown] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [showAddPetOwnerModal, setShowAddPetOwnerModal] = useState(false);
  const [specialRequestsNote, setSpecialRequestsNote] = useState("");
  const [showSpecialRequestsModal, setShowSpecialRequestsModal] = useState(false);
  const { paymentTypes, loadingPaymentTypes } = usePaymentTypes();
  const [inpatientPaymentType, setInpatientPaymentType] = useState(null);
  const [isSavingInpatient, setIsSavingInpatient] = useState(false);
  const [lengthOfStayDays, setLengthOfStayDays] = useState(0);
  const [ratePerDay, setRatePerDay] = useState(0);
  const [isIsolated, setIsIsolated] = useState(false);
  const paymentSectionRef = useRef(null);

  // Cash / credit options for the downpayment method radio
  const cashPaymentType = useMemo(
    () => (paymentTypes || []).find((pt) => (pt.label || '').toLowerCase().includes('cash')) || null,
    [paymentTypes]
  );
  const creditPaymentType = useMemo(
    () => (paymentTypes || []).find((pt) => (pt.label || '').toLowerCase().includes('credit')) || null,
    [paymentTypes]
  );

  const [ownerActiveInpatients, setOwnerActiveInpatients] = useState([]); // Store locally fetched active visits

  // Reset local active visits when owner changes
  useEffect(() => {
    setOwnerActiveInpatients([]);
  }, [selectedPetOwner]);

  // Fetch all active inpatient visits for the selected owner to ensure specific filtering
  // This covers cases where the main dashboard's "inpatientVisits" prop might only show "today's" data
  useEffect(() => {
    if (selectedPetOwner?.id && !isEdit) {
      const fetchOwnerInpatients = async () => {
        try {
          const res = await clinicAppointmentsAPI.searchInpatientVisits({
            pet_owner_id: selectedPetOwner.id,
            per_page: 50,
            sort_by: 'created_at',
            sort_order: 'desc'
          });

          if (res.data?.data?.inpatient_visits) {
            const activeVisits = res.data.data.inpatient_visits.filter(v => {
              const status = String(v.status || '').toLowerCase();
              return !['checked_out', 'completed', 'canceled', 'cancelled', 'closed', 'checkout'].includes(status);
            });
            setOwnerActiveInpatients(activeVisits);
          }
        } catch (error) {
          console.warn("Failed to fetch owner active inpatients", error);
        }
      };

      fetchOwnerInpatients();
    }
  }, [selectedPetOwner, isEdit]);

  const availablePets = useMemo(() => {
    if (!petOwnerPets || petOwnerPets.length === 0) return [];
    if (isEdit) return petOwnerPets;

    // Combine prop-based visits with locally fetched visits for maximum safety
    const allKnownInpatientVisits = [...(inpatientVisits || []), ...ownerActiveInpatients];

    return petOwnerPets.filter(pet => {

      // Exclude if pet is already in an active inpatient visit
      const hasActiveInpatient = (allKnownInpatientVisits || []).some(visit => {
        // Robust ID extraction
        const visitPetId = visit.pet_id
          || visit.originalData?.pet_id
          || (visit.pet && typeof visit.pet === 'object' ? visit.pet.id : null)
          || (visit.originalData?.pet && typeof visit.originalData.pet === 'object' ? visit.originalData.pet.id : null)
          || visit.pet; // Fallback if pet is just an ID

        if (!visitPetId) return false;

        // Ensure we're comparing numbers as IDs might be strings
        if (parseInt(visitPetId) !== parseInt(pet.id)) return false;

        const status = String(visit.status || '').toLowerCase();
        // Exclude if status is NOT one of the completed ones
        return !['checked_out', 'completed', 'canceled', 'cancelled', 'closed', 'checkout'].includes(status);
      });

      if (hasActiveInpatient) return false;

      return true;
    });
  }, [petOwnerPets, inpatientVisits, outpatientVisits, ownerActiveInpatients, isEdit]);



  // Commented out SelectPaymentModal state - will be used later
  // const [showSelectPaymentModal, setShowSelectPaymentModal] = useState(false);
  // const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  // const [paymentNotes, setPaymentNotes] = useState("");

  const dateInputRef = useRef(null);
  const datePickerRef = useRef(null);

  // Reset the nested add-pet modal whenever this modal closes
  useEffect(() => {
    if (!isOpen) setShowAddPetModal(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Initialize defaults
    let defaultState = {
      id: null,
      pet_owner: "",
      pet: "",
      vet: "",
      reason: "",
      date: "",
      time: "",
      cage_no: "",
    };

    if (isEdit && initialVisit) {
      // Prefill from initialVisit
      defaultState = {
        id: initialVisit.id,
        pet_owner: initialVisit.pet_owner || "",
        pet: typeof initialVisit.pet === 'string' ? initialVisit.pet : (initialVisit.pet?.name || ""),
        vet: initialVisit.vet || "",
        reason: initialVisit.reason || "",
        date: initialVisit.date || "",
        time: initialVisit.time || "",
        cage_no: initialVisit.cage_no || "",
        allowed_food: initialVisit.allowed_food || initialVisit.originalData?.allowed_food || "",
        allowed_fluids: initialVisit.allowed_fluids || initialVisit.originalData?.allowed_fluids || "",
      };

      // Preselect entities (disabled in edit mode, but useful for display)
      try {
        setSelectedPetOwner(initialVisit.originalData?.pet_owner || null);
      } catch { setSelectedPetOwner(null); }
      try {
        setSelectedPet(initialVisit.originalData?.pet || null);
      } catch { setSelectedPet(null); }
      try {
        setSelectedVet(initialVisit.originalData?.vet ? { user: { name: `${initialVisit.originalData?.vet?.first_name || ''} ${initialVisit.originalData?.vet?.last_name || ''}`.trim() }, user_id: initialVisit.originalData?.vet?.id } : null);
      } catch { setSelectedVet(null); }
      setLengthOfStayDays(typeof initialVisit.length_of_stay_days === 'number' ? initialVisit.length_of_stay_days : (typeof initialVisit.originalData?.length_of_stay_days === 'number' ? initialVisit.originalData.length_of_stay_days : 0));
      setRatePerDay(typeof initialVisit.rate_per_day === 'number' ? initialVisit.rate_per_day : (typeof initialVisit.originalData?.rate_per_day === 'number' ? initialVisit.originalData.rate_per_day : 0));
      setIsIsolated(initialVisit.is_isolated || initialVisit.originalData?.is_isolated || false);
      setSpecialRequestsNote(
        initialVisit.special_requests_note ||
        initialVisit.originalData?.special_requests_note ||
        ""
      );
    } else {
      // New admission — apply prefill if provided
      if (prefillPetOwner) {
        setSelectedPetOwner(prefillPetOwner);
        if (prefillPet) {
          setPetOwnerPets([prefillPet]);
          setSelectedPet(prefillPet);
        } else {
          setSelectedPet(null);
        }
      } else {
        setSelectedPetOwner(null);
        setSelectedPet(null);
      }
      setSelectedVet(null);
      setLengthOfStayDays(0);
      setRatePerDay(0);
      setIsIsolated(false);
      setSpecialRequestsNote("");
    }

    setNewInpatient(defaultState);
    setAdmitInpatientDownpayment(0.0);
    // Set pet owner search text from prefill if available
    if (!isEdit && prefillPetOwner) {
      const displayName = prefillPetOwner.display_name || prefillPetOwner.full_name ||
        `${prefillPetOwner.first_name || ''} ${prefillPetOwner.last_name || ''}`.trim();
      setPetOwnerSearch(displayName);
    } else {
      setPetOwnerSearch("");
    }
    if (!prefillPetOwner || isEdit) {
      setPetOwnerPets([]);
      setIsLoadingPets(false);
    }
    setPetOwnerSearchResults([]);
    setShowPetOwnerDropdown(false);
    setIsSearchingPetOwners(false);
    setVetSearch("");
    setShowVetDropdown(false);
    setShowTimeDropdown(false);
    setShowAddPetOwnerModal(false);
    setInpatientPaymentType(null);
    setIsSavingInpatient(false);
    // Commented out SelectPaymentModal reset - will be used later
    // setShowSelectPaymentModal(false);
    // setSelectedPaymentMethod(null);
    // setPaymentNotes("");
  }, [isOpen, isEdit, initialVisit, prefillPetOwner, prefillPet]);

  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const dayStringToIndex = (inputDay) => {
    if (inputDay === undefined || inputDay === null) return undefined;
    const d = String(inputDay).toLowerCase();
    const fullMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    if (fullMap[d] !== undefined) return fullMap[d];
    const shortMap = {
      sun: 0,
      mon: 1,
      tue: 2,
      tues: 2,
      wed: 3,
      thu: 4,
      thur: 4,
      thurs: 4,
      fri: 5,
      sat: 6,
    };
    if (shortMap[d] !== undefined) return shortMap[d];
    const first3 = d.slice(0, 3);
    if (shortMap[first3] !== undefined) return shortMap[first3];
    const num = Number(d);
    if (!Number.isNaN(num) && num >= 0 && num <= 6) return num;
    return undefined;
  };

  const generateTimeSlots = (workingHours) => {
    if (!workingHours || workingHours.length === 0) return [];
    const slots = [];
    workingHours.forEach(wh => {
      const dayIndex = dayStringToIndex(wh.day);
      if (dayIndex === undefined) return;
      const startTime = wh.opening_time || wh.from;
      const endTime = wh.closing_time || wh.to;
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
        const timeString = minutesToTime(minutes);
        slots.push({ time: timeString, display: timeString, day: wh.day, dayIndex });
      }
    });
    return slots.sort((a, b) => a.time.localeCompare(b.time));
  };

  useEffect(() => {
    const slots = generateTimeSlots(clinic?.working_hours || []);
    setAvailableTimeSlots(slots);
  }, [clinic?.working_hours]);

  // Auto-scroll to payment methods when they become visible
  useEffect(() => {
    if (!isOpen) return;
    if (admitInpatientDownpayment > 0 && paymentSectionRef.current) {
      // slight delay to ensure layout is ready (especially after loading payment types)
      const t = setTimeout(() => {
        try {
          paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch { }
      }, 50);
      return () => clearTimeout(t);
    }
  }, [admitInpatientDownpayment, isOpen, loadingPaymentTypes]);

  useEffect(() => {
    if (isOpen && Array.isArray(paymentTypes) && paymentTypes.length > 0) {
      const isCash = (t) => {
        const a = (t?.label || t?.type_display || t?.value || '').toString().toLowerCase();
        return a.includes('cash');
      };
      const defaultType = paymentTypes.find(isCash) || paymentTypes[0];
      setInpatientPaymentType(defaultType?.value || null);
    }
  }, [isOpen, paymentTypes]);

  useEffect(() => {
    if (selectedPetOwner) {
      // Use pets from the search response if available, otherwise fall back to API call
      const petsFromSearch = selectedPetOwner.pets?.list || selectedPetOwner.pets || [];

      if (petsFromSearch.length > 0) {
        // Use pets from search response (no API call needed)
        setPetOwnerPets(petsFromSearch);

        // Auto-select if there's only one available pet
        if (availablePets.length === 1) {
          setSelectedPet(availablePets[0]);
        } else {
          setSelectedPet(null);
        }
        setIsLoadingPets(false);
      } else {
        // Fallback: fetch pets via API (for cases where search didn't include pets)
        setIsLoadingPets(true);
        petAPI.getOtherPets(selectedPetOwner.id)
          .then((res) => {
            const pets = res.data?.data || [];
            setPetOwnerPets(pets);

            // Auto-select if there's only one available pet
            if (availablePets.length === 1) {
              setSelectedPet(availablePets[0]);
            } else {
              setSelectedPet(null);
            }
          })
          .catch((error) => {
            setPetOwnerPets([]);
            setSelectedPet(null);
          })
          .finally(() => {
            setIsLoadingPets(false);
          });
      }
    } else {
      setPetOwnerPets([]);
      setSelectedPet(null);
      setIsLoadingPets(false);
    }
  }, [selectedPetOwner]);


  // Pre-fill reason/complaint from last visit when pet is selected
  useEffect(() => {
    // Prefill removed: always start with a blank reason for new admissions
  }, [selectedPet, isEdit]);

  const petGuardianDisplay = selectedPetOwner
    ? getPetOwnerDisplay(selectedPetOwner)
    : (newInpatient.pet_owner || "");
  const petNameDisplay = selectedPet ? getPetNameDisplay(selectedPet) : newInpatient.pet;
  const vetDisplay = selectedVet ? getVetDisplay(selectedVet) : newInpatient.vet;
  const petPlaceholderText = !selectedPetOwner
    ? t("receptionInpatients.admit.fields.pet.placeholder.selectGuardian")
    : isLoadingPets
      ? t("receptionInpatients.admit.fields.pet.placeholder.loading")
      : availablePets.length === 0
        ? t("receptionInpatients.admit.fields.pet.placeholder.none")
        : t("receptionInpatients.admit.fields.pet.placeholder.select");
  const primaryButtonLabel = useMemo(() => {
    if (isSavingInpatient) {
      return isEdit
        ? t("receptionInpatients.admit.buttons.saving")
        : t("receptionInpatients.admit.buttons.admitting");
    }
    return isEdit
      ? t("receptionInpatients.admit.buttons.save")
      : t("receptionInpatients.admit.buttons.submit");
  }, [isEdit, isSavingInpatient, t]);

  // Allowed user types for the Pet Guardian / Client dropdown
  const allowedPetOwnerTypes = [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER];
  const filterAllowedPetOwners = (items) => (items || []).filter(item => {
    const userType = item.user_type || item.user?.user_type;
    return allowedPetOwnerTypes.includes(userType);
  });

  useEffect(() => {
    if (!petOwnerSearch.trim()) {
      setPetOwnerSearchResults([]);
      setIsSearchingPetOwners(false);
      return;
    }
    setIsSearchingPetOwners(true);
    const timeoutId = setTimeout(() => {
      clinicAPI.searchGuardians(petOwnerSearch)
        .then((response) => {
          const guardians = filterAllowedPetOwners(response.data?.data || []);

          // Also search within the local vets/staff list (Prop 'vets' contains clinic members)
          const searchTerm = petOwnerSearch.toLowerCase().trim();
          const matchingVets = filterAllowedPetOwners((vets || []).filter(vet => {
            const vetName = getVetDisplay(vet).toLowerCase();
            return vetName.includes(searchTerm);
          }).map(vet => ({
            ...vet,
            display_name: getVetDisplay(vet),
            is_staff: true,
            user_type: USER_TYPES.VETERINARIAN
          })));

          // Combine results and deduplicate by ID
          const combinedResults = [...guardians];
          matchingVets.forEach(mv => {
            // Check if this person is already in the guardian list
            if (!combinedResults.some(g => g.id === mv.id)) {
              combinedResults.push(mv);
            }
          });

          setPetOwnerSearchResults(combinedResults);
          setIsSearchingPetOwners(false);
        })
        .catch((error) => {
          // On error, fall back to local vets search
          console.error("Error searching guardians:", error);
          const searchTerm = petOwnerSearch.toLowerCase().trim();
          const matchingVets = filterAllowedPetOwners((vets || []).filter(vet => {
            const vetName = getVetDisplay(vet).toLowerCase();
            return vetName.includes(searchTerm);
          }).map(vet => ({
            ...vet,
            display_name: getVetDisplay(vet),
            is_staff: true,
            user_type: USER_TYPES.VETERINARIAN
          })));

          setPetOwnerSearchResults(matchingVets);
          setIsSearchingPetOwners(false);
        });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [petOwnerSearch]);

  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) {
      return availableTimeSlots;
    }
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    const selectedDayIndex = selectedDateObj.getDay();
    const isToday = selectedDateObj.toDateString() === today.toDateString();
    let filteredSlots = availableTimeSlots.filter(slot => slot.dayIndex === selectedDayIndex);
    if (isToday) {
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      filteredSlots = filteredSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.time.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;
        return slotTimeInMinutes >= (currentTimeInMinutes + 15);
      });
    }
    return filteredSlots;
  };

  useEffect(() => {
    if (isOpen && dateInputRef.current) {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
      const availableDays = clinic?.working_hours
        ? clinic.working_hours
          .map(wh => dayStringToIndex(wh.day))
          .filter((idx) => idx !== undefined)
        : [];
      const disableFunction = clinic?.working_hours?.length > 0 ? [
        function (date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date < today) return true;
          const dayOfWeek = date.getDay();
          const isDisabled = !availableDays.includes(dayOfWeek);
          return isDisabled;
        }
      ] : [
        function (date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDate = date < today;
          return isPastDate;
        }
      ];
      datePickerRef.current = window.flatpickr ? window.flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        disable: disableFunction,
        onChange: function (selectedDates, dateStr) {
          setNewInpatient(prev => ({ ...prev, date: dateStr, time: "" }));
          setShowTimeDropdown(false);
        }
      }) : null;
    }
    return () => {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
    };
  }, [isOpen, clinic?.working_hours]);

  if (!isOpen) return null;

  return (
    <div
      className="reception-modal-overlay"
      dir={i18n.dir()}
      lang={i18n.language}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)'
      }}
    >
      <style>
        {`
          .reception-modal input[type="number"]::-webkit-outer-spin-button,
          .reception-modal input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .reception-modal input[type="number"] {
            -moz-appearance: textfield;
            appearance: textfield;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        className="reception-modal"
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '90%',
          maxWidth: '800px',
          height: '95vh',
          maxHeight: '95vh',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="reception-modal-header" style={{
          padding: '24px 32px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: isRTL ? 'row-reverse' : 'row',
          background: 'linear-gradient(to right, #ffffff, #f9fafb)'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '22px',
            fontWeight: '700',
            color: '#111827',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '28px' }} aria-hidden="true">{isEdit ? '✏️' : '🏥'}</span>
            {isEdit ? t("receptionInpatients.admit.header.edit") : t("receptionInpatients.admit.header.new")}
          </h2>
          <button
            className="reception-modal-close"
            onClick={onClose}
            style={{
              background: '#f3f4f6',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6b7280',
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#fee2e2';
              e.target.style.color = '#ef4444';
              e.target.style.transform = 'rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#6b7280';
              e.target.style.transform = 'rotate(0deg)';
            }}
          >
            ✕
          </button>
        </div>
        <form
          className="reception-modal-form"
          onSubmit={(e) => { e.preventDefault(); }}
          style={{
            overflow: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
          }}
        >
          <div style={{ flex: 1, overflow: 'auto', paddingBottom: '12px' }}>
            {/* Row 1: Pet Guardian and Pet */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#3b82f6' }}>👤</span> {t("receptionInpatients.admit.fields.petGuardian.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  <div
                    className="reception-modal-input"
                    onClick={() => !isEdit && setShowPetOwnerDropdown(!showPetOwnerDropdown)}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      border: '2px solid #f3f4f6',
                      borderRadius: '12px',
                      fontSize: '15px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      backgroundColor: isEdit ? '#f9fafb' : 'white',
                      cursor: isEdit ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxShadow: isEdit ? 'none' : '0 1px 2px rgba(0,0,0,0.02)'
                    }}
                    onMouseEnter={(e) => {
                      if (!isEdit) e.target.style.borderColor = '#e5e7eb';
                    }}
                    onFocus={(e) => {
                      if (!isEdit) {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!isEdit) {
                        e.target.style.borderColor = '#f3f4f6';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <span style={{ color: selectedPetOwner ? '#111827' : '#9ca3af', fontWeight: selectedPetOwner ? '500' : '400' }}>
                      {selectedPetOwner ? (getPetOwnerDisplay(selectedPetOwner) || t("receptionInpatients.admit.fields.petGuardian.placeholder")) : t("receptionInpatients.admit.fields.petGuardian.placeholder")}
                    </span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{isEdit ? '🔒' : '▼'}</span>
                  </div>
                  {showPetOwnerDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        zIndex: 10,
                        maxHeight: '300px',
                        overflow: 'hidden',
                        marginTop: '8px',
                        animation: 'fadeIn 0.2s ease-out'
                      }}
                    >
                      <div style={{ padding: '12px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionInpatients.admit.fields.petGuardian.searchPlaceholder")}
                          value={petOwnerSearch}
                          onChange={(e) => {
                            setPetOwnerSearch(e.target.value);
                            if (selectedPetOwner && e.target.value) {
                              setSelectedPetOwner(null);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {isSearchingPetOwners ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                            <div
                              style={{
                                display: 'inline-block',
                                width: '20px',
                                height: '20px',
                                border: '2px solid #e5e7eb',
                                borderTop: '2px solid #3b82f6',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            ></div>
                            <div style={{ marginTop: '8px', fontSize: '14px' }}>
                              {t("receptionInpatients.admit.loading.guardians")}
                            </div>
                          </div>
                        ) : petOwnerSearchResults.length > 0 ? (
                          petOwnerSearchResults.map((g) => {
                            const displayName = getPetOwnerDisplay(g);
                            const lastRelation = g.last_completed_appointment_relation;
                            const lastVisitLabel = lastRelation
                              ? t("receptionInpatients.admit.fields.petGuardian.lastVisit", {
                                date: formatDate(lastRelation.appointment_at),
                                status: formatStatusLabel(lastRelation.status)
                              })
                              : null;
                            return (
                              <div
                                key={g.id}
                                style={{
                                  cursor: "pointer",
                                  padding: "12px 18px",
                                  borderBottom: '1px solid #f3f4f6',
                                  transition: 'all 0.2s',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#374151'
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedPetOwner(g);
                                  setShowPetOwnerDropdown(false);
                                  setPetOwnerSearchResults([]);
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#f0f7ff';
                                  e.target.style.color = '#2563eb';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = '#374151';
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '500', color: '#111827' }}>
                                    {displayName || t("receptionInpatients.admit.fields.petGuardian.placeholder")}
                                  </div>
                                  {lastVisitLabel && (
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                      {lastVisitLabel}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : petOwnerSearch.trim() ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                            {t("receptionInpatients.admit.fields.petGuardian.empty")}
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                              <button
                                className="modern-action-btn vet-button-color"
                                onClick={() => {
                                  setShowPetOwnerDropdown(false);
                                  setPetOwnerSearch('');
                                  setPetOwnerSearchResults([]);
                                  setShowAddPetOwnerModal(true);
                                }}
                              >
                                {t("receptionInpatients.admit.fields.petGuardian.add")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                            {t("receptionInpatients.admit.fields.petGuardian.startTyping")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>

              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#3b82f6' }}>🐾</span> {t("receptionInpatients.admit.fields.pet.label")}
                </span>
                <select
                  className="reception-modal-input"
                  value={selectedPet ? selectedPet.name : ""}
                  onChange={(e) => {
                    const name = e.target.value;
                    if (name === "__add_new__") {
                      setShowAddPetModal(true);
                      return;
                    }
                    const pet = petOwnerPets.find(p => p.name === name);
                    setSelectedPet(pet || null);
                  }}
                  disabled={isEdit || !selectedPetOwner || isLoadingPets}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    border: '2px solid #f3f4f6',
                    borderRadius: '12px',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: (isEdit || !selectedPetOwner || isLoadingPets) ? '#f9fafb' : 'white',
                    appearance: 'none',
                    cursor: (isEdit || !selectedPetOwner || isLoadingPets) ? 'not-allowed' : 'pointer',
                    boxShadow: (isEdit || !selectedPetOwner || isLoadingPets) ? 'none' : '0 1px 2px rgba(0,0,0,0.02)'
                  }}
                  onFocus={(e) => {
                    if (!isEdit && selectedPetOwner && !isLoadingPets) {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    if (!isEdit && selectedPetOwner && !isLoadingPets) {
                      e.target.style.borderColor = '#f3f4f6';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  <option value="">
                    {isEdit ? (typeof initialVisit.pet === 'string' ? initialVisit.pet : (initialVisit.pet?.name || "")) :
                      (!selectedPetOwner ? t("receptionInpatients.admit.fields.pet.placeholder.selectGuardian") :
                        isLoadingPets ? t("receptionInpatients.admit.fields.pet.placeholder.loading") :
                          petOwnerPets.length === 0 ? t("receptionInpatients.admit.fields.pet.placeholder.none") :
                            t("receptionInpatients.admit.fields.pet.placeholder.select"))}
                  </option>
                  {!isLoadingPets &&
                    petOwnerPets.map((p) => (
                      <option key={p.id} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  {!isEdit && selectedPetOwner && !isLoadingPets && (
                    <option value="__add_new__">
                      ➕ {t("receptionInpatients.admit.fields.pet.addNew") || 'Add New Pet'}
                    </option>
                  )}
                </select>
                {isLoadingPets && (
                  <div
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      pointerEvents: 'none'
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #e5e7eb',
                        borderTop: '2px solid #3b82f6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}
                    ></div>
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        fontWeight: '500'
                      }}
                    >
                      {t("receptionInpatients.admit.loading.pets")}
                    </span>
                  </div>
                )}
              </label>
            </div>

            {/* Row 2: Vet and Reason */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#3b82f6' }}>👨‍⚕️</span> {t("receptionInpatients.admit.fields.vet.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  <div
                    className="reception-modal-input"
                    onClick={() => !isEdit && setShowVetDropdown(!showVetDropdown)}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      border: '2px solid #f3f4f6',
                      borderRadius: '12px',
                      fontSize: '15px',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      cursor: isEdit ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: isEdit ? '#f9fafb' : 'white',
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                      boxShadow: isEdit ? 'none' : '0 1px 2px rgba(0,0,0,0.02)'
                    }}
                    aria-disabled={isEdit}
                    onFocus={(e) => {
                      if (!isEdit) {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!isEdit) {
                        e.target.style.borderColor = '#f3f4f6';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <span style={{ color: selectedVet ? '#111827' : '#9ca3af', fontWeight: selectedVet ? '500' : '400' }}>
                      {selectedVet ? (getVetDisplay(selectedVet) || t("receptionInpatients.admit.fields.vet.placeholder")) : t("receptionInpatients.admit.fields.vet.placeholder")}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {isEdit ? '🔒' : (showVetDropdown ? '▲' : '▼')}
                    </span>
                  </div>
                  {!isEdit && showVetDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '8px',
                      animation: 'fadeIn 0.2s ease-out'
                    }}>
                      <div style={{ padding: '12px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionInpatients.admit.fields.vet.searchPlaceholder")}
                          value={vetSearch}
                          onChange={(e) => setVetSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {vets.filter((v) => {
                          const displayName = v.user?.name || v.name || ((v.first_name || '') + ' ' + (v.last_name || '')).trim() || `Vet ${v.user_id || v.id || 'Unknown'}`;
                          return displayName.toLowerCase().includes(vetSearch.toLowerCase());
                        }).length > 0 ? (
                          vets
                            .filter((v) => {
                              const displayName = v.user?.name || v.name || ((v.first_name || '') + ' ' + (v.last_name || '')).trim() || `Vet ${v.user_id || v.id || 'Unknown'}`;
                              return displayName.toLowerCase().includes(vetSearch.toLowerCase());
                            })
                            .map((v) => {
                              const displayName = v.user?.name || v.name || ((v.first_name || '') + ' ' + (v.last_name || '')).trim() || `Vet ${v.user_id || v.id || 'Unknown'}`;
                              return (
                                <div
                                  key={v.user_id || v.id}
                                  style={{
                                    cursor: "pointer",
                                    padding: "12px 18px",
                                    borderBottom: '1px solid #f3f4f6',
                                    transition: 'all 0.2s',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedVet(v);
                                    setShowVetDropdown(false);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f0f7ff';
                                    e.target.style.color = '#2563eb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = '#374151';
                                  }}
                                >
                                  {displayName}
                                </div>
                              );
                            })
                        ) : vetSearch.trim() ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                            {t("receptionInpatients.admit.fields.vet.empty")}
                          </div>
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                            {t("receptionInpatients.admit.fields.vet.startTyping")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>

              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#3b82f6' }}>📝</span> {t("receptionInpatients.admit.fields.reason.label")}
                </span>
                <input
                  type="text"
                  className="reception-modal-input"
                  value={newInpatient.reason}
                  onChange={(e) => setNewInpatient((a) => ({ ...a, reason: e.target.value }))}
                  placeholder={t("receptionInpatients.admit.fields.reason.placeholder")}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    border: '2px solid #f3f4f6',
                    borderRadius: '12px',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f3f4f6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
            </div>
            {/* 
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                📅 Admission Date
              </span>
              <input
                ref={dateInputRef}
                type="text"
                className="reception-modal-input"
                placeholder="Select date..."
                readOnly
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  transition: 'all 0.2s',
                  outline: 'none',
                  cursor: isEdit ? 'not-allowed' : 'pointer',
                  backgroundColor: isEdit ? '#f9fafb' : 'white'
                }}
                disabled={isEdit}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
            <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                ⏰ Admission Time
              </span>
              <div style={{ position: 'relative' }}>
                <div
                  className="reception-modal-input"
                  onClick={() => {
                    if (isEdit || !newInpatient.date) return;
                    const availableSlots = getAvailableTimeSlotsForDate(newInpatient.date);
                    if (availableSlots.length > 0) setShowTimeDropdown(!showTimeDropdown);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    cursor: isEdit ? 'not-allowed' : (!newInpatient.date ? 'not-allowed' : (getAvailableTimeSlotsForDate(newInpatient.date).length > 0 ? 'pointer' : 'not-allowed')),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: isEdit ? '#f9fafb' : (!newInpatient.date || getAvailableTimeSlotsForDate(newInpatient.date).length === 0 ? '#f9fafb' : 'white')
                  }}
                >
                  <span style={{ color: newInpatient.time ? '#111827' : '#9ca3af' }}>
                    {!newInpatient.date
                      ? t("receptionInpatients.admit.dropdown.selectDateFirstShort")
                      : (newInpatient.time || t("receptionInpatients.admit.dropdown.selectTime"))}
                  </span>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {showTimeDropdown ? '▲' : '▼'}
                  </span>
                </div>
                {showTimeDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    zIndex: 10,
                    maxHeight: '200px',
                    overflow: 'auto',
                    marginTop: '4px'
                  }}>
                    {getAvailableTimeSlotsForDate(newInpatient.date).length > 0 ? (
                      <>
                        <div style={{ padding: '8px 16px', backgroundColor: '#f0f9ff', borderBottom: '1px solid #e0f2fe', fontSize: '12px', color: '#0369a1', fontWeight: '500' }}>
                          {t("receptionInpatients.admit.dropdown.availableSlots", {
                            date: formatDate(newInpatient.date)
                          })}
                        </div>
                        {getAvailableTimeSlotsForDate(newInpatient.date).map((slot, index) => (
                          <div
                            key={index}
                            style={{
                              cursor: "pointer",
                              padding: "12px 16px",
                              borderBottom: '1px solid #f3f4f6',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor: slot.time === newInpatient.time ? '#e0f2fe' : 'transparent',
                              borderLeft: slot.time === newInpatient.time ? '3px solid #3b82f6' : '3px solid transparent',
                              color: slot.time === newInpatient.time ? '#1d4ed8' : '#111827',
                              fontWeight: slot.time === newInpatient.time ? '600' : '400'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setNewInpatient(prev => ({ ...prev, time: slot.time }));
                              setShowTimeDropdown(false);
                            }}
                            onMouseEnter={(e) => {
                              if (slot.time !== newInpatient.time) {
                                e.target.style.backgroundColor = '#f9fafb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (slot.time !== newInpatient.time) {
                                e.target.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <span style={{ fontSize: '16px', fontWeight: '500' }}>
                              {slot.display}
                            </span>
                            <span style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>
                              {getDayShort(slot.dayIndex)}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                        {newInpatient.date ?
                          (() => {
                            const selectedDateObj = new Date(newInpatient.date);
                            const today = new Date();
                            const isSelectedDateToday = selectedDateObj.toDateString() === today.toDateString();
                            return isSelectedDateToday
                              ? t("receptionInpatients.admit.dropdown.noSlotsToday")
                              : t("receptionInpatients.admit.dropdown.noSlotsDay");
                          })() :
                          t("receptionInpatients.admit.dropdown.selectDateFirst")
                        }
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>
          </div> */}


            {/* Row 3: Feeding and Water Orders */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#f59e0b' }}>🍖</span> {t("receptionInpatients.admit.fields.feedingOrder.label", { defaultValue: "Feeding order" })}
                </span>
                <select
                  className="reception-modal-input"
                  value={newInpatient.allowed_food}
                  onChange={(e) => setNewInpatient((a) => ({ ...a, allowed_food: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    border: '2px solid #f3f4f6',
                    borderRadius: '12px',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: 'white',
                    appearance: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#f59e0b';
                    e.target.style.boxShadow = '0 0 0 4px rgba(245, 158, 11, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f3f4f6';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">{t("receptionInpatients.admit.fields.feedingOrder.placeholder", { defaultValue: "Select feeding order..." })}</option>
                  <option value="allowed">{t("receptionInpatients.admit.fields.feedingOrder.options.allowed", { defaultValue: "Allowed" })}</option>
                  <option value="not_allowed">{t("receptionInpatients.admit.fields.feedingOrder.options.not_allowed", { defaultValue: "Not Allowed" })}</option>
                  <option value="special">{t("receptionInpatients.admit.fields.feedingOrder.options.special", { defaultValue: "Special" })}</option>
                </select>
              </label>

              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#0ea5e9' }}>💧</span> {t("receptionInpatients.admit.fields.waterOrder.label", { defaultValue: "Water order" })}
                </span>
                <select
                  className="reception-modal-input"
                  value={newInpatient.allowed_fluids}
                  onChange={(e) => setNewInpatient((a) => ({ ...a, allowed_fluids: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    border: '2px solid #f3f4f6',
                    borderRadius: '12px',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: 'white',
                    appearance: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0ea5e9';
                    e.target.style.boxShadow = '0 0 0 4px rgba(14, 165, 233, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f3f4f6';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">{t("receptionInpatients.admit.fields.waterOrder.placeholder", { defaultValue: "Select water order..." })}</option>
                  <option value="allowed">{t("receptionInpatients.admit.fields.waterOrder.options.allowed", { defaultValue: "Allowed" })}</option>
                  <option value="not_allowed">{t("receptionInpatients.admit.fields.waterOrder.options.not_allowed", { defaultValue: "Not Allowed" })}</option>
                  <option value="special">{t("receptionInpatients.admit.fields.waterOrder.options.special", { defaultValue: "Special" })}</option>
                </select>
              </label>
            </div>

            {/* Row 4: Rate and Downpayment */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#10b981' }}>💵</span> {t("receptionInpatients.admit.fields.ratePerDay.label")}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="reception-modal-input"
                  value={ratePerDay}
                  onChange={(e) => setRatePerDay(Math.max(0, Number(e.target.value)))}
                  placeholder={t("receptionInpatients.admit.fields.ratePerDay.placeholder", { defaultValue: "0.00" })}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    border: '2px solid #f3f4f6',
                    borderRadius: '12px',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f3f4f6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>

              <label className="reception-modal-label" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#10b981' }}>💰</span> {t("receptionInpatients.admit.fields.downpayment.label")}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="reception-modal-input"
                  value={admitInpatientDownpayment}
                  onChange={(e) => setAdmitInpatientDownpayment(Math.max(0, Number(e.target.value)))}
                  placeholder={t("receptionInpatients.admit.fields.downpayment.placeholder", { defaultValue: "0.00" })}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    border: '2px solid #f3f4f6',
                    borderRadius: '12px',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f3f4f6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
            </div>

            {/* Downpayment method — only shown when a downpayment is entered */}
            {admitInpatientDownpayment > 0 && (cashPaymentType || creditPaymentType) && (
              <div ref={paymentSectionRef} style={{ marginBottom: '24px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#10b981' }}>💳</span> {t("receptionInpatients.admit.fields.paymentMethod.label", { defaultValue: "Downpayment Method" })}
                </span>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {[cashPaymentType, creditPaymentType].filter(Boolean).map((pt) => (
                    <label
                      key={pt.value}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '14px 18px',
                        border: `2px solid ${String(inpatientPaymentType) === String(pt.value) ? '#10b981' : '#f3f4f6'}`,
                        borderRadius: '12px',
                        backgroundColor: String(inpatientPaymentType) === String(pt.value) ? '#ecfdf5' : 'white',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '500',
                        color: '#111827',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <input
                        type="radio"
                        name="inpatientDownpaymentMethod"
                        checked={String(inpatientPaymentType) === String(pt.value)}
                        onChange={() => setInpatientPaymentType(pt.value)}
                        style={{ accentColor: '#10b981', width: '16px', height: '16px' }}
                      />
                      {pt.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            className="reception-modal-actions"
            style={{
              display: 'flex',
              gap: '16px',
              flexDirection: isRTL ? 'row-reverse' : 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '24px 32px',
              borderTop: '1px solid #f3f4f6',
              marginTop: 'auto',
              flexWrap: 'nowrap',
              background: 'white' // Changed from #f9fafb as requested
            }}
          >
            <button
              type="button"
              className="reception-action-btn"
              onClick={onClose}
              style={{
                padding: '12px 28px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: 'white',
                color: '#4b5563',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="reception-action-btn"
              onClick={() => setShowSpecialRequestsModal(true)}
              style={{
                padding: '12px 28px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: 'white',
                color: '#4b5563',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '18px' }}>📋</span>
              {t("receptionInpatients.admit.buttons.specialRequests", { defaultValue: "Special Requests" })}
            </button>
            <button
              type="button"
              className="reception-action-btn"
              onClick={async () => {
                try {
                  setIsSavingInpatient(true);
                  if (isEdit) {
                    const payload = {};
                    if (newInpatient.cage_no !== undefined) payload.cage_no = newInpatient.cage_no;
                    if (newInpatient.reason !== undefined) payload.reason = newInpatient.reason;
                    if (newInpatient.allowed_food !== undefined) payload.allowed_food = newInpatient.allowed_food;
                    if (newInpatient.allowed_fluids !== undefined) payload.allowed_fluids = newInpatient.allowed_fluids;
                    if (specialRequestsNote && specialRequestsNote.trim()) {
                      payload.special_requests_note = specialRequestsNote.trim();
                    }
                    if (isIsolated !== undefined) payload.is_isolated = isIsolated;
                    if (lengthOfStayDays && Number(lengthOfStayDays) > 0) {
                      payload.length_of_stay_days = Number(lengthOfStayDays);
                    }
                    if (ratePerDay !== undefined) {
                      payload.rate_per_day = Number(ratePerDay);
                    }
                    await inpatientAPI.updateInpatientVisit(initialVisit.id, payload);
                    showSuccessToast(t("receptionInpatients.admit.toasts.updateSuccess"));
                    const updatedRow = {
                      ...initialVisit,
                      cage_no: payload.cage_no !== undefined ? payload.cage_no : initialVisit.cage_no,
                      reason: payload.reason !== undefined ? payload.reason : initialVisit.reason,
                      allowed_food: payload.allowed_food !== undefined ? payload.allowed_food : (initialVisit.allowed_food || initialVisit.originalData?.allowed_food),
                      allowed_fluids: payload.allowed_fluids !== undefined ? payload.allowed_fluids : (initialVisit.allowed_fluids || initialVisit.originalData?.allowed_fluids),
                      length_of_stay_days: payload.length_of_stay_days !== undefined ? payload.length_of_stay_days : initialVisit.length_of_stay_days,
                      originalData: {
                        ...(initialVisit.originalData || {}),
                        cage_no: payload.cage_no !== undefined ? payload.cage_no : initialVisit.originalData?.cage_no,
                        reason: payload.reason !== undefined ? payload.reason : initialVisit.originalData?.reason,
                        allowed_food: payload.allowed_food !== undefined ? payload.allowed_food : initialVisit.originalData?.allowed_food,
                        allowed_fluids: payload.allowed_fluids !== undefined ? payload.allowed_fluids : initialVisit.originalData?.allowed_fluids,
                        length_of_stay_days: payload.length_of_stay_days !== undefined ? payload.length_of_stay_days : initialVisit.originalData?.length_of_stay_days,
                        rate_per_day: ratePerDay,
                        is_isolated: isIsolated,
                      }
                    };
                    if (onVisitUpdated) onVisitUpdated(updatedRow);
                    onClose();
                  } else {
                    if (!selectedPetOwner) { showErrorToast(t("receptionInpatients.admit.validation.petGuardian")); return; }
                    if (!selectedPet) { showErrorToast(t("receptionInpatients.admit.validation.pet")); return; }
                    if (!selectedVet) { showErrorToast(t("receptionInpatients.admit.validation.vet")); return; }
                    const payload = {
                      pet_owner_id: selectedPetOwner.id,
                      pet_id: selectedPet.id,
                      vet_id: selectedVet.user_id,
                      clinic_id: clinic?.id,
                      downpayment: admitInpatientDownpayment || 0,
                      reason: newInpatient.reason,
                      cage_no: newInpatient.cage_no,
                      rate_per_day: ratePerDay,
                      is_isolated: isIsolated,
                      date: newInpatient.date || new Date().toISOString().split('T')[0],
                      time: newInpatient.time || new Date().toTimeString().slice(0, 5),
                      allowed_food: newInpatient.allowed_food,
                      allowed_fluids: newInpatient.allowed_fluids,
                      feeding_order: newInpatient.allowed_food,
                      water_order: newInpatient.allowed_fluids,
                    };
                    if (specialRequestsNote && specialRequestsNote.trim()) {
                      payload.special_requests_note = specialRequestsNote.trim();
                    }
                    if (admitInpatientDownpayment > 0) {
                      const finalPaymentType = inpatientPaymentType || (paymentTypes?.length > 0 ? (paymentTypes.find(t => (t.label || '').toLowerCase().includes('cash'))?.value || paymentTypes[0].value) : null);
                      if (finalPaymentType) {
                        payload.payment_type_id = finalPaymentType;
                      }
                    }
                    if (lengthOfStayDays && Number(lengthOfStayDays) > 0) {
                      payload.length_of_stay_days = Number(lengthOfStayDays);
                    }
                    const response = await inpatientAPI.createInpatientVisit(payload);
                    showSuccessToast(t("receptionInpatients.admit.toasts.createSuccess"));
                    const createdVisitId = response?.data?.data?.id || response?.data?.id;
                    const displayRow = {
                      id: createdVisitId || Date.now(),
                      pet_owner: getPetOwnerDisplay(selectedPetOwner),
                      pet: getPetNameDisplay(selectedPet),
                      vet: getVetDisplay(selectedVet),
                      status: "admitted",
                      originalData: response?.data?.data || response?.data
                    };
                    if (onVisitCreated) onVisitCreated(displayRow);
                    onClose();
                  }
                } catch (error) {
                  showErrorToast(error.response?.data?.message || (isEdit ? t("receptionInpatients.admit.toasts.updateFailed") : t("receptionInpatients.admit.toasts.createFailed")));
                } finally {
                  setIsSavingInpatient(false);
                }
              }}
              disabled={isSavingInpatient}
              style={{
                padding: '12px 32px',
                border: 'none',
                borderRadius: '12px',
                background: isSavingInpatient ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isSavingInpatient ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isSavingInpatient ? 'none' : '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                padding: '12px 36px'
              }}
              onMouseEnter={(e) => {
                if (!isSavingInpatient) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -2px rgba(59, 130, 246, 0.1)';
                  e.target.style.filter = 'brightness(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSavingInpatient) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)';
                  e.target.style.filter = 'none';
                }
              }}
            >
              {isSavingInpatient ? (
                <div style={{
                  width: '18px',
                  height: '18px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
              ) : (
                <span style={{ fontSize: '18px' }}>💾</span>
              )}
              {primaryButtonLabel}
            </button>
          </div>
        </form >

        <SpecialRequestsNoteModal
          isOpen={showSpecialRequestsModal}
          onClose={() => setShowSpecialRequestsModal(false)}
          value={specialRequestsNote}
          onSave={(note) => setSpecialRequestsNote(note)}
        />

        <QuickAddPetModal
          isOpen={showAddPetModal}
          onClose={() => setShowAddPetModal(false)}
          onPetAdded={(newPet) => {
            setPetOwnerPets((prev) => [...prev, newPet]);
            setSelectedPet(newPet);
            setShowAddPetModal(false);
          }}
          ownerId={selectedPetOwner?.id}
          ownerName={selectedPetOwner ? getPetOwnerDisplay(selectedPetOwner) : ''}
          userTypeColor="#3b82f6"
        />

        {/* Commented out SelectPaymentModal - will be used later */}
        {/* 
        <SelectPaymentModal
          isOpen={showSelectPaymentModal}
          onClose={() => setShowSelectPaymentModal(false)}
          onSave={(data) => {
            setSelectedPaymentMethod(data.method);
            setPaymentNotes(data.notes || "");
            setShowSelectPaymentModal(false);
            // Automatically proceed with admission after payment selection
            setTimeout(async () => {
              try {
                setIsSavingInpatient(true);
                if (!selectedPetOwner) { showErrorToast(t("receptionInpatients.admit.validation.petGuardian")); return; }
                if (!selectedPet) { showErrorToast(t("receptionInpatients.admit.validation.pet")); return; }
                if (!selectedVet) { showErrorToast(t("receptionInpatients.admit.validation.vet")); return; }
                const payload = {
                  pet_owner_id: selectedPetOwner.id,
                  pet_id: selectedPet.id,
                  vet_id: selectedVet.user_id,
                  clinic_id: clinic?.id,
                  downpayment: admitInpatientDownpayment || 0,
                  reason: newInpatient.reason,
                  cage_no: newInpatient.cage_no,
                  allowed_food: newInpatient.allowed_food,
                  allowed_fluids: newInpatient.allowed_fluids,
                };
                if (specialRequestsNote && specialRequestsNote.trim()) {
                  payload.special_requests_note = specialRequestsNote.trim();
                }
                if (admitInpatientDownpayment > 0 && data.method) {
                  payload.payment_type_id = data.method;
                }
                if (data.notes && data.notes.trim()) {
                  payload.payment_note = data.notes.trim();
                }
                if (lengthOfStayDays && Number(lengthOfStayDays) > 0) {
                  payload.length_of_stay_days = Number(lengthOfStayDays);
                }
                const response = await inpatientAPI.createInpatientVisit(payload);
                showSuccessToast(t("receptionInpatients.admit.toasts.createSuccess"));
                const createdRaw = response?.data || {};
                const createdData = createdRaw?.data || createdRaw;
                const createdVisitId = createdData?.id
                  || createdData?.visit?.id
                  || createdData?.inpatient_visit?.id
                  || createdData?.inpatient?.id
                  || createdRaw?.id;
                const originalData = { ...createdData };
                if (createdVisitId && !originalData.id) {
                  originalData.id = createdVisitId;
                }
                const displayRow = {
                  id: createdVisitId || Date.now(),
                  pet_owner: selectedPetOwner ? (getPetOwnerDisplay(selectedPetOwner) || t("receptionInpatients.admit.fields.petGuardian.placeholder")) : newInpatient.pet_owner,
                  pet: selectedPet ? (getPetNameDisplay(selectedPet) || newInpatient.pet) : newInpatient.pet,
                  vet: selectedVet ? (getVetDisplay(selectedVet) || t("receptionInpatients.admit.fields.vet.generic", { id: selectedVet.user_id })) : newInpatient.vet,
                  date: newInpatient.date,
                  time: newInpatient.time,
                  cage_no: newInpatient.cage_no,
                  downpayment: admitInpatientDownpayment || 0,
                  status: "admitted",
                  allowed_food: newInpatient.allowed_food,
                  allowed_fluids: newInpatient.allowed_fluids,
                  originalData: originalData
                };
                if (onVisitCreated) onVisitCreated(displayRow);
                onClose();
              } catch (error) {
                console.error('Error creating inpatient visit:', error);
                showErrorToast(error.response?.data?.message || t("receptionInpatients.admit.toasts.createFailed"));
              } finally {
                setIsSavingInpatient(false);
              }
            }, 100);
          }}
          title="Select Payment Method for Downpayment"
          price={admitInpatientDownpayment}
        />
        */}
      </div >
    </div >
  );
};

export default AdmitInpatientModal;


